// Generic OpenAI-compatible chat client — routes to a local router (OmniRoute,
// 9Router, any LAN box) browser-direct, and to the hosted providers the bridge
// allowlists (bridge/qlipper_bridge.py's ALLOWED_LLM_HOSTS) through the same-origin
// /yt/or proxy, preserving the zero-key OpenRouter path (openrouter + empty key =
// server-side key).
import { isLocalBase, BRIDGE_HOSTS } from './registry.js';

function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^\[|\]$/g, '');
  } catch {
    return '';
  }
}

function messageFrom(data) {
  return data?.choices?.[0]?.message?.content;
}

// One line of guidance shown in OUR UI before the first request to a local router
// (Chrome 142+ gates https-page → localhost behind a Local Network Access prompt; if
// nobody clicks Allow the request hangs, then aborts, and reads as "gak kejangkau").
// Never use a native alert/confirm/prompt for this.
export const LOCAL_NETWORK_PERMISSION_HINT = 'Chrome akan minta izin akses jaringan lokal. Klik Izinkan supaya Qlipper bisa terhubung ke router kamu.';

// Show the hint while the base URL is local and nothing has reached the router yet
// in this panel (a green connection test or a real discovery hides it).
export function shouldShowLocalNetworkHint(baseUrl, { conn = null, discovery = null } = {}) {
  if (!isLocalBase(baseUrl)) return false;
  if (conn && conn.ok) return false;
  if (discovery && !discovery.usedDefaults) return false;
  return true;
}

// Shared copy for "local router unreachable" — used both by the network-error throw
// below and by LiteLlmSettings' "Tes koneksi" status-0 badge, so the two never drift.
export function localRouterHint(base) {
  return `Router lokal di ${base} gak kejangkau. Pastikan OmniRoute/9Router jalan di port itu, izinkan akses jaringan lokal kalau Chrome nanya, dan router harus kirim header CORS.`;
}

/**
 * @param {object} opts
 * @param {string} opts.presetId
 * @param {string} opts.baseUrl
 * @param {string} [opts.apiKey]
 * @param {string} opts.model
 * @param {Array}  opts.messages
 * @param {number} [opts.maxTokens]
 * @returns {Promise<{content:string, model:string, raw:object}>}
 */
export async function chatCompletion({ presetId, baseUrl, apiKey, model, messages, maxTokens } = {}) {
  const base = String(baseUrl || '').replace(/\/$/, '');
  // 9Router/custom ship with an empty default model (and custom with an empty Base
  // URL) — without this check the request either sends `"model": ""` (opaque 400 from
  // the upstream) or, for a blank baseUrl, fetches the relative '/chat/completions' on
  // the SPA's OWN origin (404). Fail fast here with a message the user can act on.
  if (!base) throw new Error('Base URL kosong.');
  if (!model) throw new Error('Model belum dipilih.');
  const host = hostnameOf(base);
  const local = isLocalBase(base);
  const useBridge = !local && presetId !== 'custom' && BRIDGE_HOSTS.includes(host);
  // stream:false is explicit on purpose: 9Router (and other routers) answer with SSE
  // `data:` chunks when the field is absent, which res.json() can't parse — the
  // analysis would die with "jawaban tidak terbaca". Harmless for every
  // OpenAI-compatible upstream, including the /yt/or bridge path.
  const body = { model, messages, stream: false, ...(maxTokens ? { max_tokens: maxTokens } : {}) };

  if (useBridge) {
    const headers = { 'Content-Type': 'application/json', 'X-Llm-Base-Url': base };
    if (apiKey) headers['X-Byok-Key'] = apiKey;
    const res = await globalThis.fetch('/yt/or', { method: 'POST', headers, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.error) {
      throw new Error(`AI server ${res.status}: ${(data.error?.message || data.error || JSON.stringify(data)).toString().slice(0, 200)}`);
    }
    return { content: messageFrom(data), model: data?.model, raw: data };
  }

  const headers = { 'Content-Type': 'application/json' };
  if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
  let res;
  try {
    res = await globalThis.fetch(base + '/chat/completions', { method: 'POST', headers, body: JSON.stringify(body) });
  } catch (e) {
    if (local && e instanceof TypeError) {
      throw new Error(localRouterHint(base), { cause: e });
    }
    throw e;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) {
    throw new Error(`AI server ${res.status}: ${(data.error?.message || data.error || JSON.stringify(data)).toString().slice(0, 200)}`);
  }
  return { content: messageFrom(data), model: data?.model, raw: data };
}
